// Примитивы эффектов. Все функции, которым может понадобиться решение игрока, —
// генераторы: они `yield` объект-запрос (prompt), а UI отвечает через game.answer().
import type {
  CardInst, CardRef, ChooseCardsKind, DdkId, Game, GainWhere, Iid, Player, PlayerId, PlayerZone, Prompt, PromptAnswer, PromptOption, CardDef,
} from '../types.ts';
import { startLifeOf } from './mutators.ts';
import {
  card, log, enemies, leftOf, rightOf, clockwiseFrom, drawCards, gainChips, spendChips, gainCard, gainLimp, trashCard,
  removeFrom, maxLife, revealTop, controlled, isWand, emit, ddkDef, ddkPenalty, rngOf, player, isBespredel, discardCards, markHandKnown,
} from './state.ts';
import { RULES, rv, redirectKills } from './rules.ts';
import { shuffle, randInt } from './rng.ts';
import * as H from './hooks.ts';
import * as T from './tokens.ts';

/** Эффект: генератор, который задаёт игроку вопросы (Prompt) и получает ответы (PromptAnswer) */
export type Eff<R = void> = Generator<Prompt, R, PromptAnswer>;

/** Контекст эффекта: кто и какой картой действует, плюс модификаторы атаки/смерти */
export interface EffectCtx {
  source: Player | null;
  card?: CardInst | null;
  isAttack?: boolean;
  bespredel?: boolean;
  /** текст атаки без карты (для запроса защиты) */
  text?: string;
  /** суммарный нанесённый урон за атаку (считается в afterDamage) */
  dealt?: number;
  /** «если он от этого подох» */
  onKill?: (victim: Player) => Eff;
  noPrize?: boolean;
  instantWin?: boolean;
  noRedirect?: boolean;
  /** атака перенаправлена на атакующего этим игроком: урон атакующему наносит он (правила, с. 17) — и он убийца */
  redirectedBy?: Player;
  /** Мортал Комбо: убийца выбирает жетон из трёх */
  ddkChoice?: boolean;
  target?: Player;
  defenseCard?: CardInst;
  killer?: Player | null;
  fromDeath?: boolean;
}

// ---------- запросы к игроку ----------

export interface ChoosePlayerOpts {
  optional?: boolean;
  /** ожидаемый урон каждому кандидату (выбор цели атаки) */
  damage?: Record<PlayerId, number>;
  /** выбор во вред цели (не урон): боты целятся как при атаке */
  harm?: boolean;
}

export function* choosePlayer(_G: Game, who: Player, candidates: Player[], title: string, { optional = false, damage, harm }: ChoosePlayerOpts = {}): Eff<Player | null> {
  if (!candidates.length) return null;
  const only = candidates[0];
  if (candidates.length === 1 && only && !optional) return only;
  const ans = yield { type: 'choosePlayer', who: who.id, candidates: candidates.map((p) => p.id), title, optional, ...(damage ? { damage } : {}), ...(harm ? { harm } : {}) };
  if (typeof ans !== 'number') return null;
  return candidates.find((p) => p.id === ans) ?? null;
}

/** Урон, который цель получит от этой атаки с учётом модификаторов атакующего (жетоны, свойства, постоянки) */
export function previewDamage(G: Game, ctx: EffectCtx, target: Player, n: number): number {
  return n > 0 ? H.modifyDamage(G, ctx, target, n) : 0;
}

/**
 * Выбор цели атаки уроном: в вопросе — имя карты и ожидаемый урон (у каждого кандидата свой: модификаторы
 * могут зависеть от цели). «Сырная палочка: кому 1 урона?» с жетоном «+4 палочками» превращается в «кому 5 урона?».
 */
export function* pickTarget(G: Game, p: Player, ctx: EffectCtx, candidates: Player[], n: number, title?: string): Eff<Player | null> {
  const damage: Record<PlayerId, number> = {};
  for (const t of candidates) damage[t.id] = previewDamage(G, ctx, t, n);
  const vals = new Set(Object.values(damage));
  const [one] = vals;
  const name = ctx.card ? card(ctx.card).name : 'Атака';
  const what = vals.size === 1 && one != null ? `кому ${one} урона?` : 'кому урон?';
  return yield* choosePlayer(G, p, candidates, title ?? `${name}: ${what}`, { damage });
}

export interface ChooseCardsOpts {
  min?: number;
  max?: number;
  title: string;
  zoneLabel?: string;
  optional?: boolean;
  kind?: ChooseCardsKind;
}

export function* chooseCards(_G: Game, who: Player, cards: CardInst[], opts: ChooseCardsOpts): Eff<CardInst[]> {
  const { max = 1, title, zoneLabel = '', optional = false, kind } = opts;
  let { min = 1 } = opts;
  if (!cards.length) return [];
  if (optional) min = 0;
  if (cards.length <= min && !optional) return cards.slice(0, Math.max(min, Math.min(max, cards.length)));
  const ans = yield {
    type: 'chooseCards', who: who.id, cards: cards.map((c) => c.iid), defs: cards.map(cardRef),
    min: Math.min(min, cards.length), max, title, zoneLabel, ...(kind ? { kind } : {}),
  };
  const set = new Set<Iid>(Array.isArray(ans) ? ans : []);
  return cards.filter((c) => set.has(c.iid));
}

/** Дополнительные поля запроса: карты-контекст, которые UI покажет прямо в вопросе */
export interface PromptExtra {
  cards?: CardRef[];
}

export const cardRef = (c: CardInst): CardRef => ({ iid: c.iid, cid: c.cid });

export function* chooseOption(_G: Game, who: Player, options: PromptOption[], title: string, extra: PromptExtra = {}): Eff<string> {
  const first = options[0];
  if (!first) throw new Error(`chooseOption без вариантов: ${title}`);
  if (options.length === 1) return first.id;
  const ans = yield { type: 'chooseOption', who: who.id, options, title, ...extra };
  if (typeof ans === 'string' && options.some((o) => o.id === ans)) return ans;
  return first.id;
}

export function* confirm(_G: Game, who: Player, title: string, yes = 'Да', no = 'Нет', extra: PromptExtra = {}): Eff<boolean> {
  const ans = yield { type: 'chooseOption', who: who.id, options: [{ id: 'yes', label: yes }, { id: 'no', label: no }], title, ...extra };
  return ans === 'yes';
}

export function* info(_G: Game, who: Player | null, title: string, text: string, extra: PromptExtra = {}): Eff {
  yield { type: 'info', who: who == null ? null : who.id, title, text, ...extra };
}

/** Короткая заметка игроку без отдельного экрана: в журнал + событие (UI показывает всплывашкой) */
export function note(G: Game, p: Player | null, text: string): void {
  log(G, `${p ? p.name + ': ' : ''}${text}`);
  emit(G, { type: 'note', pid: p ? p.id : null, text });
}

export function* chooseNumber(_G: Game, who: Player, min: number, max: number, title: string, extra: PromptExtra = {}): Eff<number> {
  if (min === max) return min;
  const ans = yield { type: 'chooseNumber', who: who.id, min, max, title, ...extra };
  return Math.max(min, Math.min(max, Number(ans) || 0));
}

/** Подпись жетона дохлого колдуна в вопросах: штраф и текст */
export function ddkLabel(id: DdkId): string {
  const d = ddkDef(id);
  return `(${ddkPenalty(d)} ПО)${d.text ? ' ' + d.text : ' без текста'}`;
}

// ---------- мощь, карты, чипсины ----------

export function addPower(G: Game, n: number, label = ''): void {
  if (!G.turn || !n) return;
  G.turn.power += n;
  log(G, `⚡ +${n} мощи${label ? ` (${label})` : ''}`);
  emit(G, { type: 'power', pid: G.turn.player, n, label });
}

export function* draw(G: Game, p: Player, n: number): Eff<CardInst[]> {
  const d = drawCards(G, p, n);
  if (d.length) log(G, `${p.name} берёт ${d.length} карт(ы)`);
  return d;
}

export function* discardFromHand(G: Game, p: Player, n: number, { title = 'Выбери карты для сброса', random = false } = {}): Eff<CardInst[]> {
  let cards: CardInst[];
  if (random) {
    const hand = [...p.hand];
    cards = [];
    for (let i = 0; i < n && hand.length; i++) {
      const c = hand.splice(randInt(rngOf(G), hand.length), 1)[0];
      if (c) cards.push(c);
    }
  } else {
    cards = yield* chooseCards(G, p, p.hand, { min: Math.min(n, p.hand.length), max: n, title, zoneLabel: 'рука', kind: 'discard' });
  }
  if (cards.length) discardCards(G, p, cards);
  else log(G, `${p.name}: рука пуста — сбрасывать нечего`);
  return cards;
}

export function* trashFrom(G: Game, p: Player, zone: PlayerZone, cards: CardInst[], { min = 0, max = 1, title = 'Выбери карты для уничтожения' } = {}): Eff<CardInst[]> {
  if (!cards.length) {
    log(G, `${p.name}: нет подходящих карт — ничего не уничтожено`);
    return [];
  }
  const chosen = yield* chooseCards(G, p, cards, { min, max, title, optional: min === 0, kind: 'trash' });
  for (const c of chosen) {
    removeFrom(p[zone], c.iid);
    trashCard(G, c, p);
    log(G, `${p.name} уничтожает «${card(c).name}»`);
  }
  return chosen;
}

export interface TrashHandOrDiscardOpts {
  min?: number;
  max?: number;
  title?: string;
  filter?: ((c: CardInst) => boolean) | null;
}

export function* trashFromHandOrDiscard(G: Game, p: Player, { min = 0, max = 1, title = 'Уничтожь карту с руки или из сброса', filter = null }: TrashHandOrDiscardOpts = {}): Eff<CardInst[]> {
  const pool = [...p.hand, ...p.discard].filter((c) => !filter || filter(c));
  const chosen = yield* chooseCards(G, p, pool, { min, max, title, optional: min === 0, zoneLabel: 'рука/сброс', kind: 'trash' });
  for (const c of chosen) {
    if (!removeFrom(p.hand, c.iid)) removeFrom(p.discard, c.iid);
    trashCard(G, c, p);
    log(G, `${p.name} уничтожает «${card(c).name}»`);
  }
  return chosen;
}

/** Прямая установка жизней (обмен, «свести к N», «к максимуму»): не урон и не лечение — смерть не наступает */
export function setLifeTo(G: Game, p: Player, n: number, src?: string): void {
  const target = Math.max(0, Math.min(n, maxLife(p)));
  const delta = target - p.life;
  if (!delta) return;
  p.life = target;
  log(G, `${p.name}: жизни ${delta > 0 ? '+' : ''}${delta} (${target})${src ? ` — ${src}` : ''}`);
  emit(G, { type: 'life', pid: p.id, delta, life: target, ...(src ? { src } : {}) });
}

export function heal(G: Game, p: Player, n: number): void {
  const before = p.life;
  p.life = Math.min(maxLife(p), p.life + n);
  if (p.life > before) {
    log(G, `${p.name} накручивает ${p.life - before} жизней (${p.life})`);
    emit(G, { type: 'heal', pid: p.id, n: p.life - before, life: p.life });
  }
}

/**
 * Получить карту с триггерами «получив …»: сначала выбор места (жетоны свойств, Сувенирный ларёк, Зад в будущее),
 * затем сама карта и её триггеры (Вялая башня, жетон «волшебник → чипсина»).
 */
export function* gainWithTriggers(G: Game, p: Player, c: CardInst, { fromMarket = false, bought = false, where = 'discard' as GainWhere } = {}): Eff {
  const def = card(c);
  if (where === 'discard' && !isBespredel(c)) {
    const dests = H.gainDestinations(G, p, c, fromMarket, bought);
    if (dests.length) {
      const ans = yield* chooseOption(G, p, [...dests, { id: 'discard', label: 'Оставить в сбросе' }], `Куда положить «${def.name}»?`, { cards: [cardRef(c)] });
      if (ans === 'hand' || ans === 'deckTop') where = ans;
    }
  }
  gainCard(G, p, c, where);
  if (G.turn && G.turn.player === p.id) {
    G.turn.gainedThisTurn.push(c.cid);
    if (def.type === 'заклинание') G.turn.spellsGained++;
  }
  yield* H.onGain(G, p, c);
}

export function* gainFromMarket(G: Game, p: Player, filter: (d: CardDef, c: CardInst) => boolean, title: string, { where = 'discard' as GainWhere } = {}): Eff<CardInst | null> {
  const pool = [...G.market, ...G.legendMarket].filter((c) => filter(card(c), c));
  if (!pool.length) return null;
  const [c] = yield* chooseCards(G, p, pool, { min: 1, max: 1, title, zoneLabel: 'барахолка' });
  if (!c) return null;
  yield* takeFromMarket(G, p, c, where);
  return c;
}

export function* takeFromMarket(G: Game, p: Player, c: CardInst, where: GainWhere = 'discard'): Eff<CardInst> {
  if (!removeFrom(G.market, c.iid)) removeFrom(G.legendMarket, c.iid);
  const chips = G.marketChips[c.iid] ?? 0;
  if (chips) {
    delete G.marketChips[c.iid];
    p.chips += chips;
    log(G, `${p.name} забирает ${chips} чипсин, лежавших на «${card(c).name}»`);
    emit(G, { type: 'chips', pid: p.id, n: chips, src: `лежали на «${card(c).name}»` });
  }
  yield* gainWithTriggers(G, p, c, { fromMarket: true, where });
  return c;
}

// ---------- урон и смерть ----------

export function* damage(G: Game, ctx: EffectCtx, target: Player, n: number): Eff<number> {
  if (n <= 0) return 0;
  const src = ctx.source;
  n = H.modifyDamage(G, ctx, target, n);
  target.life -= n;
  log(G, `${target.name} получает ${n} урона (${Math.max(0, target.life)})`);
  emit(G, { type: 'damage', pid: target.id, n, from: src ? src.id : null, cid: ctx.card ? ctx.card.cid : null, life: Math.max(0, target.life), bespredel: !!ctx.bespredel });
  if (src && src.id !== target.id) target.grudge = { ...target.grudge, [src.id]: (target.grudge?.[src.id] ?? 0) + n };
  if (ctx.isAttack) ctx.dealt = (ctx.dealt ?? 0) + n; // суммарный урон атаки — для «сколько нанёс» (Шлёпалочка, Смурфинёр, Тронадо)
  H.afterDamage(G, ctx, target, n);
  if (target.life < 1) {
    yield* die(G, ctx, target);
  }
  return n;
}

export function* die(G: Game, ctx: EffectCtx, target: Player): Eff {
  // атакующий подох от своей же атаки, которую ему перенаправили (Сальный шут, Кондуктор, Бензопила «Дружок»): это не
  // самоубийство — урон «обратно ему в лицо» нанёс перенаправивший, он и убийца (с v16; раньше приз никому не шёл)
  const redirector = ctx.redirectedBy && ctx.source && ctx.source.id === target.id && redirectKills(G) ? ctx.redirectedBy : null;
  const killer = redirector ?? (ctx.source && ctx.source.id !== target.id ? ctx.source : null);
  log(G, `☠ ${target.name} подох!`);
  emit(G, { type: 'die', pid: target.id, killer: killer ? killer.id : null });
  if (killer) {
    // убийство в этот ход — заслуга хода атакующего, а перенаправивший убил не своим ходом
    if (G.turn && !redirector) G.turn.killsThisTurn++;
    if (!ctx.noPrize && G.prizeOwner !== killer.id) {
      // приз уже у убийцы — не переезжает, событие и запись не нужны
      G.prizeOwner = killer.id;
      log(G, `${killer.name} получает главный приз Крутагидона`);
      emit(G, { type: 'prize', pid: killer.id });
    }
    // мгновенная победа — свойство карты атакующего: перенаправленная атака её перенаправившему не дарит
    if (ctx.instantWin && !redirector) G.instantWin = killer.id;
  }
  // эффекты «если он от этого подох» срабатывают и при самоубийстве (приз при этом не даётся)
  if (ctx.source && ctx.onKill) yield* ctx.onKill(target);
  // правила, стр. 14: после смерти «снова становится 20 жизней, если только на ЖДК не сказано иного»,
  // но у лошары свой потолок («не может иметь больше 15 жизней») — воскрешение его не обходит
  // (до версии 6 воскрешение ставило 20 напрямую, минуя потолок лошары — партии тех версий играются так же)
  target.life = rv(G) >= 6
    ? Math.min(H.baseLife(G, target, startLifeOf(G)), maxLife(target))
    : target.loser ? RULES.startLife : H.baseLife(G, target, RULES.startLife);
  let skip = false;
  // Браталити: убийца может не дать жертве жетон
  if (killer && G.turn && G.turn.brataliti && killer.id === G.turn.player) {
    G.turn.brataliti = false; // «в следующий раз, когда убьёшь» — предложение делается один раз
    const yes = yield* confirm(G, killer, 'Браталити: не давать жертве жетон дохлого колдуна (ты получишь 1 жетон и 1 легенду)?');
    if (yes) {
      skip = true;
      yield* giveDdk(G, ctx, killer);
      const pool = [...G.legendMarket];
      if (pool.length) {
        const [c] = yield* chooseCards(G, killer, pool, { min: 1, max: 1, title: 'Получи 1 легенду', zoneLabel: 'барахолка легенд' });
        if (c) yield* takeFromMarket(G, killer, c, 'discard');
      }
    }
  }
  if (!skip) {
    if (ctx.ddkChoice && killer) {
      // Мортал Комбо: взять 3 жетона, отдать 1 по выбору, остальные вернуть
      const pile = G.ddkPile ?? [];
      const taken = pile.splice(-3, 3);
      if (taken.length) {
        const opts = taken.map((id) => ({ id, label: ddkLabel(id) }));
        const pick = yield* chooseOption(G, killer, opts, 'Какой жетон дохлого колдуна отдать жертве?');
        const rest = taken.filter((id) => id !== pick);
        pile.unshift(...rest);
        yield* applyDdk(G, ctx, target, pick, { fromDeath: true, killer });
      }
    } else {
      yield* giveDdk(G, ctx, target, { fromDeath: true, killer });
    }
  }
  if (target.loser) target.life = Math.min(target.life, RULES.loserMaxLife);
}

export interface DdkOpts {
  fromDeath?: boolean;
  killer?: Player | null;
  /** жетон передан этим игроком (не из стопки) */
  from?: Player;
}

/** Выдать жетон дохлого колдуна из стопки и применить его */
export function* giveDdk(G: Game, ctx: EffectCtx, target: Player, opts: DdkOpts = {}): Eff<DdkId | null> {
  const tid = G.ddkPile?.pop();
  if (!tid) {
    log(G, 'Жетоны дохлых колдунов закончились');
    return null;
  }
  yield* applyDdk(G, ctx, target, tid, opts);
  return tid;
}

export function* applyDdk(G: Game, ctx: EffectCtx, target: Player, tid: DdkId, opts: DdkOpts = {}): Eff {
  target.ddk.push(tid);
  const t = ddkDef(tid);
  log(G, `${target.name} получает жетон дохлого колдуна${opts.from ? ` от ${opts.from.name}` : ''}: «${t.text || '—'}»`);
  // Отдельного экрана «посмотри жетон» нет: текст уходит в журнал/ленту событий (в хотсите игрок увидит его,
  // когда телефон дойдёт до него), а эффекты, требующие решения, сами зададут вопрос.
  emit(G, { type: 'ddk', pid: target.id, tid, text: t.text || '', vp: ddkPenalty(t), ...(opts.from ? { from: opts.from.id } : {}) });
  const fx = T.DDK_EFFECTS[t.key];
  if (fx) yield* fx(G, { ...ctx, target, killer: opts.killer ?? null, fromDeath: !!opts.fromDeath }, target, t);
}

// ---------- атака / защита ----------

/** Какие карты защиты может сыграть цель */
export function defenseOptions(G: Game, target: Player): CardInst[] {
  return target.hand.filter((c) => {
    const d = card(c);
    if (!d.defense) return false;
    const can = T.DEFENSE_CAN[d.id];
    return !can || can(G, target, c);
  });
}

export interface OnHitInfo {
  redirected: boolean;
  /** кто перенаправил атаку */
  by: Player;
}

export type OnHit = (target: Player, hit: Player[], info?: OnHitInfo) => Eff;

export interface AttackOpts {
  targets: Player[];
  unavoidable?: boolean;
  bespredel?: boolean;
  onHit: OnHit;
  /** базовый урон цели (до модификаторов) — чтобы защищающийся видел, от чего защищается */
  damage?: (target: Player) => number;
}

interface Decision {
  t: Player;
  avoided: boolean;
  redirect?: boolean;
}

/** Проводит атаку по списку целей; возвращает тех, кого атака достигла */
export function* attack(G: Game, ctx: EffectCtx, opts: AttackOpts): Eff<Player[]> {
  const { targets, onHit } = opts;
  let unavoidable = opts.unavoidable ?? false;
  const attacker = ctx.source;
  const hit: Player[] = [];
  ctx.dealt = 0;
  // Бензопила «Дружок»: следующую атаку нельзя избежать; жетон свойства: атаки палочек нельзя избежать
  if (attacker && G.turn && attacker.id === G.turn.player && !opts.bespredel) {
    if (G.turn.nextUnavoidable) {
      unavoidable = true;
      G.turn.nextUnavoidable = false;
    }
    if (H.wandsUnavoidable(attacker) && ctx.card && isWand(ctx.card)) unavoidable = true;
    if (H.allAttacksUnavoidable(attacker)) unavoidable = true;
  }
  // фаза решений о защите: для беспредела — все по часовой от активного; для обычной атаки — каждая цель сразу
  const decide = function* (t: Player, { redirectedFrom = null as Player | null } = {}): Eff<Decision> {
    const redirected = !!redirectedFrom;
    if (unavoidable) return { t, avoided: false };
    // карта защиты играется только в чужой ход (кроме беспредела и защиты от перенаправленной атаки)
    if (G.turn && t.id === G.turn.player && !opts.bespredel && !redirected) return { t, avoided: false };
    if (G.turn && G.turn.noDefense.includes(t.id) && attacker && attacker.id === G.turn.player) {
      if (defenseOptions(G, t).length) note(G, t, 'не может защищаться в этот ход (Восстание из зада)');
      return { t, avoided: false };
    }
    const opts2 = defenseOptions(G, t);
    if (!opts2.length) {
      // в руке есть карты с «Защитой», но их условие не выполнено (нет чипсин/жизней) — объяснить человеку, почему не спросили
      const locked = t.hand.filter((c) => card(c).defense);
      if (locked.length && !t.bot) note(G, t, `защита не сработает: ${locked.map((c) => `«${card(c).name}»`).join(', ')} — условие карты сейчас не выполнить`);
      return { t, avoided: false };
    }
    emit(G, { type: 'defendPrompt', pid: t.id, from: attacker ? attacker.id : null, cid: ctx.card ? ctx.card.cid : null });
    // при перенаправлении урон — тот, что предназначался перенаправившему (см. attackDamage)
    const dmgFor = opts.damage ? previewDamage(G, ctx, t, opts.damage(redirectedFrom ?? t)) : null;
    const ans = yield {
      type: 'defend', who: t.id, attacker: attacker ? attacker.id : null,
      card: ctx.card ? cardRef(ctx.card) : null,
      text: ctx.card ? card(ctx.card).text : (ctx.text ?? ''),
      cards: opts2.map((c) => c.iid),
      ...(dmgFor != null ? { damage: dmgFor } : {}),
    };
    if (typeof ans !== 'number') return { t, avoided: false };
    const dc = t.hand.find((c) => c.iid === ans);
    if (!dc || !opts2.includes(dc)) return { t, avoided: false };
    const ddef = card(dc);
    const fx = T.DEFENSE_EFFECTS[ddef.id] ?? T.DEFAULT_DEFENSE;
    const res = yield* fx(G, { ...ctx, target: t, defenseCard: dc }, t, dc);
    if (res === false) return { t, avoided: false };
    emit(G, { type: 'defend', pid: t.id, iid: dc.iid, cid: dc.cid, from: attacker ? attacker.id : null });
    log(G, `${t.name} защищается картой «${ddef.name}»`);
    return { t, avoided: true, redirect: typeof res === 'object' && res.redirect };
  };
  emit(G, { type: 'attack', from: attacker ? attacker.id : null, targets: targets.map((t) => t.id), cid: ctx.card ? ctx.card.cid : null, bespredel: !!opts.bespredel, unavoidable });
  if (opts.bespredel) {
    // беспредел с барахолки перенаправить некуда (атакующего нет); разыгранный картой врага — враг и есть
    // источник атаки: перенаправление бьёт по нему, и он может защититься сам
    const decisions: Decision[] = [];
    const redirectedBy = new Map<Player, Player>();
    for (const t of targets) {
      const d = yield* decide(t);
      if (d.redirect && attacker && attacker.id !== t.id && !ctx.noRedirect) {
        const d2 = yield* decide(attacker, { redirectedFrom: t });
        if (!d2.avoided) {
          log(G, `Атака перенаправлена на ${attacker.name}`);
          emit(G, { type: 'redirect', from: t.id, to: attacker.id });
          redirectedBy.set(attacker, t);
          decisions.push({ t: attacker, avoided: false });
        }
        continue;
      }
      decisions.push(d);
    }
    for (const d of decisions) {
      if (!d.avoided) hit.push(d.t);
    }
    for (const t of hit) {
      const by = redirectedBy.get(t);
      if (by) ctx.redirectedBy = by;
      yield* onHit(t, hit, by ? { redirected: true, by } : undefined);
      delete ctx.redirectedBy;
    }
  } else {
    for (const t of targets) {
      const d = yield* decide(t);
      if (d.redirect && attacker && attacker.id !== t.id && !ctx.noRedirect) {
        // перенаправление атаки на атакующего: атакующий может защититься сам
        const d2 = yield* decide(attacker, { redirectedFrom: t });
        if (!d2.avoided) {
          log(G, `Атака перенаправлена на ${attacker.name}`);
          emit(G, { type: 'redirect', from: t.id, to: attacker.id });
          ctx.redirectedBy = t;
          yield* onHit(attacker, [attacker], { redirected: true, by: t });
          delete ctx.redirectedBy;
          hit.push(attacker);
        }
      } else if (!d.avoided) {
        hit.push(t);
        yield* onHit(t, [t]);
      }
    }
  }
  H.afterAttack(G, ctx);
  return hit;
}

export interface AttackDamageExtra {
  unavoidable?: boolean;
  bespredel?: boolean;
  /** после нанесения урона цели (в том числе если она не подохла) */
  after?: (target: Player) => Eff;
}

/** Удобный вариант: атака уроном (число или функция от цели) */
export function* attackDamage(G: Game, ctx: EffectCtx, targets: Player[], dmg: number | ((target: Player) => number), extra: AttackDamageExtra = {}): Eff<Player[]> {
  return yield* attack(G, ctx, {
    targets,
    unavoidable: extra.unavoidable ?? false,
    bespredel: extra.bespredel ?? false,
    damage: (t) => (typeof dmg === 'function' ? dmg(t) : dmg),
    onHit: function* (t, _hit, info) {
      // при перенаправлении урон — тот, что предназначался перенаправившему
      const src = info && info.redirected ? info.by : t;
      const n = typeof dmg === 'function' ? dmg(src) : dmg;
      yield* damage(G, ctx, t, n);
      if (extra.after) yield* extra.after(t);
    },
  });
}

// ---------- выбор целей ----------

export function strongestEnemies(G: Game, p: Player): Player[] {
  const en = enemies(G, p);
  const m = Math.max(...en.map((e) => e.life));
  return en.filter((e) => e.life === m);
}
export function weakestEnemies(G: Game, p: Player): Player[] {
  const en = enemies(G, p);
  const m = Math.min(...en.map((e) => e.life));
  return en.filter((e) => e.life === m);
}
export function* pickStrongest(G: Game, p: Player): Eff<Player | null> {
  return yield* choosePlayer(G, p, strongestEnemies(G, p), 'Выбери самого могучего врага');
}
export function* pickWeakest(G: Game, p: Player): Eff<Player | null> {
  return yield* choosePlayer(G, p, weakestEnemies(G, p), 'Выбери самого хилого врага');
}
export function* pickEnemy(G: Game, p: Player, title = 'Выбери врага'): Eff<Player | null> {
  return yield* choosePlayer(G, p, enemies(G, p), title);
}
export function* pickWizard(G: Game, p: Player, title = 'Выбери колдуна', opts: ChoosePlayerOpts = {}): Eff<Player | null> {
  return yield* choosePlayer(G, p, [...G.players], title, opts);
}
export function leftRight(G: Game, p: Player): Player[] {
  const l = leftOf(G, p);
  const r = rightOf(G, p);
  return l.id === r.id ? [l] : [l, r];
}

// ---------- разное ----------

export function* becomeLoser(G: Game, p: Player): Eff<boolean> {
  if (p.loser) return false;
  p.loser = true;
  p.life = Math.min(p.life, RULES.loserMaxLife);
  log(G, `${p.name} становится лошарой!`);
  emit(G, { type: 'loser', pid: p.id, on: true });
  return true;
}
export function* stopLoser(G: Game, p: Player): Eff<boolean> {
  if (!p.loser) return false;
  p.loser = false;
  log(G, `${p.name} больше не лошара`);
  emit(G, { type: 'loser', pid: p.id, on: false });
  return true;
}

/** Сыграть верхнюю карту колоды врага (шальная магия и др.) */
export function* playTopOfEnemyDeck(G: Game, me: Player, enemy: Player): Eff<CardInst | null> {
  const c = revealTop(G, enemy);
  if (!c) {
    log(G, `Колода ${enemy.name} пуста — карта не сыграна`);
    return null;
  }
  enemy.deck.pop();
  const def = card(c);
  log(G, `${me.name} играет верхнюю карту колоды ${enemy.name}: «${def.name}»`);
  emit(G, { type: 'play', pid: me.id, iid: c.iid, cid: c.cid, from: enemy.id });
  if (isBespredel(c)) {
    log(G, `«${def.name}» — беспредел: уничтожается без эффекта`);
    yield* info(G, me, `С колоды ${enemy.name}: беспредел`, 'Беспредел с чужой колоды не играется — карта уничтожается без эффекта.', { cards: [cardRef(c)] });
    trashCard(G, c);
    return c;
  }
  // игрок должен увидеть, что именно ему досталось, до вопросов самой карты
  yield* info(G, me, `С колоды ${enemy.name}: «${def.name}»`, def.text, { cards: [cardRef(c)] });
  if (def.permanent) {
    // новый владелец
    me.permanents.push(c);
  } else {
    me.inPlay.push(c);
    c.returnTo = enemy.id; // в конце хода вернуть в сброс владельца
  }
  yield* T.playCardEffect(G, me, c, { fromEnemyDeck: true });
  return c;
}

export function* revealHand(G: Game, p: Player): Eff {
  markHandKnown(p, p.hand);
  yield* info(G, null, `Рука игрока ${p.name}`, p.hand.length ? `${p.hand.length} карт` : '(пусто)', { cards: p.hand.map(cardRef) });
}

export { shuffle, randInt, gainChips, spendChips, gainLimp, gainCard, trashCard, removeFrom, controlled, enemies, leftOf, rightOf, clockwiseFrom, emit, player };
